import React, { useState, useEffect } from 'react';
import { FaUserGraduate, FaSearch, FaFilter } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatDate, calculateAge } from '../../utils/helpers';
import Modal from '../common/Modal';

/**
 * StudentList component for mentors to view and manage their assigned students
 */
const StudentList = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  /**
   * Fetch students assigned to the mentor
   */
  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/mentors/children');
      setStudents(response.data.data);
    } catch (error) {
      console.error('Error fetching students:', error);
      showToast('Failed to load students', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calculate progress percentage from mentorship progress entries
   */
  const calculateProgressPercentage = (student) => {
    // If the student has a direct progress property as a number, use it
    if (typeof student.progress === 'number') {
      return student.progress;
    }
    
    // If student has mentorshipProgress array
    if (student.mentorshipProgress && student.mentorshipProgress.length > 0) {
      // Get the most recent progress entry
      const latestProgress = student.mentorshipProgress[student.mentorshipProgress.length - 1];
      
      if (latestProgress && latestProgress.metrics) {
        // Calculate average of all metrics
        const metrics = latestProgress.metrics;
        const values = [
          metrics.skillDevelopment,
          metrics.completedTasks,
          metrics.consistencyEffort,
          metrics.communication,
          metrics.initiativeProblemSolving,
          metrics.growthReflection,
          metrics.collaborationTeamwork,
          metrics.behaviorAttitude,
          metrics.learningDevelopment,
          metrics.personalGrowth
        ].filter(val => typeof val === 'number'); // Filter out undefined values
        
        if (values.length > 0) {
          // Calculate the average and convert to percentage (metrics are on a scale of 0-10)
          return Math.round(values.reduce((sum, val) => sum + val, 0) / values.length * 10);
        }
      }
    }
    
    // Default progress if no data available
    return 0;
  };

  /**
   * Get the student's grade from class or gradeLevel property
   */
  const getStudentGrade = (student) => {
    if (student.class && student.class.gradeLevel) {
      return student.class.gradeLevel;
    }
    return student.gradeLevel || 'N/A';
  };

  /**
   * Get the student's status (active/inactive)
   */
  const getStudentStatus = (student) => {
    // Default to active if no status property
    return student.status || 'active';
  };

  /**
   * Filter students based on search term and filter
   */
  const filteredStudents = students.filter(student => {
    // Apply search filter
    const searchMatch = 
      student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.school && student.school.name && student.school.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Apply category filter
    if (filter === 'all') return searchMatch;
    if (filter === 'active' && getStudentStatus(student) === 'active') return searchMatch;
    if (filter === 'inactive' && getStudentStatus(student) !== 'active') return searchMatch;
    
    return false;
  });

  /**
   * Handle student selection to view details
   */
  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setIsModalOpen(true);
  };

  /**
   * Close the detail modal
   */
  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">My Students</h2>
        <div className="flex items-center space-x-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Search students..."
              className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
          </div>
          <div className="relative">
            <select
              className="form-select pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Students</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <FaFilter className="absolute left-3 top-3 text-gray-400" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {filteredStudents.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <FaUserGraduate className="mx-auto text-5xl text-gray-300 mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No students found</h3>
              <p className="text-gray-500">
                {searchTerm || filter !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : 'You have not been assigned any students yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-3 px-4 text-left font-medium text-gray-600">Name</th>
                    <th className="py-3 px-4 text-left font-medium text-gray-600">Age</th>
                    <th className="py-3 px-4 text-left font-medium text-gray-600">School</th>
                    <th className="py-3 px-4 text-left font-medium text-gray-600">Grade</th>
                    <th className="py-3 px-4 text-left font-medium text-gray-600">Progress</th>
                    <th className="py-3 px-4 text-left font-medium text-gray-600">Status</th>
                    <th className="py-3 px-4 text-left font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredStudents.map((student) => (
                    <tr key={student._id} className="hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          {student.profileImage ? (
                            <img 
                              src={student.profileImage} 
                              alt={`${student.firstName} ${student.lastName}`}
                              className="w-10 h-10 rounded-full mr-3"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                              <span className="text-blue-600 font-medium">
                                {student.firstName.charAt(0)}{student.lastName.charAt(0)}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-800">
                              {student.firstName} {student.lastName}
                            </p>
                            <p className="text-sm text-gray-500">{student.email || 'No email'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-700">
                        {student.dateOfBirth ? calculateAge(student.dateOfBirth) : 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-gray-700">
                        {student.school && student.school.name ? student.school.name : 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-gray-700">
                        {getStudentGrade(student)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className="bg-blue-600 h-2.5 rounded-full"
                            style={{ width: `${calculateProgressPercentage(student)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{calculateProgressPercentage(student)}% Complete</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          getStudentStatus(student) === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {getStudentStatus(student) === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleStudentSelect(student)}
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Student Detail Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title="Student Details"
        size="lg"
      >
        {selectedStudent && (
          <div className="p-4">
            <div className="flex items-center mb-6">
              {selectedStudent.profileImage ? (
                <img 
                  src={selectedStudent.profileImage} 
                  alt={`${selectedStudent.firstName} ${selectedStudent.lastName}`}
                  className="w-16 h-16 rounded-full mr-4"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span className="text-blue-600 text-xl font-medium">
                    {selectedStudent.firstName.charAt(0)}{selectedStudent.lastName.charAt(0)}
                  </span>
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold">{selectedStudent.firstName} {selectedStudent.lastName}</h2>
                <p className="text-gray-600">
                  {selectedStudent.dateOfBirth ? `Age: ${calculateAge(selectedStudent.dateOfBirth)}` : ''}
                  {selectedStudent.gender ? ` • ${selectedStudent.gender.charAt(0).toUpperCase() + selectedStudent.gender.slice(1)}` : ''}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">School Information</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p><span className="font-medium">School:</span> {selectedStudent.school ? selectedStudent.school.name : 'N/A'}</p>
                  <p><span className="font-medium">Grade Level:</span> {getStudentGrade(selectedStudent)}</p>
                  <p><span className="font-medium">Class:</span> {selectedStudent.class ? selectedStudent.class.name : 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-3">Parent Information</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  {selectedStudent.parent ? (
                    <>
                      <p><span className="font-medium">Name:</span> {selectedStudent.parent.firstName} {selectedStudent.parent.lastName}</p>
                      <p><span className="font-medium">Email:</span> {selectedStudent.parent.email || 'N/A'}</p>
                      <p><span className="font-medium">Phone:</span> {selectedStudent.parent.phoneNumber || 'N/A'}</p>
                    </>
                  ) : (
                    <p>No parent information available</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3">Progress Overview</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="mb-4">
                  <p className="font-medium mb-2">Overall Progress</p>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className="bg-blue-600 h-4 rounded-full"
                      style={{ width: `${calculateProgressPercentage(selectedStudent)}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{calculateProgressPercentage(selectedStudent)}% Complete</p>
                </div>
                
                {selectedStudent.mentorshipProgress && selectedStudent.mentorshipProgress.length > 0 && (
                  <div>
                    <p className="font-medium mb-2">Recent Assessments</p>
                    <div className="max-h-60 overflow-y-auto">
                      {selectedStudent.mentorshipProgress.slice().reverse().map((entry, index) => (
                        <div key={index} className="mb-3 border-b pb-3">
                          <p className="text-sm font-medium">
                            {new Date(entry.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                          {entry.metrics && entry.metrics.comment && (
                            <p className="text-sm italic mt-1">{entry.metrics.comment}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={closeModal}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
              >
                Close
              </button>
              <button
                onClick={() => {
                  closeModal();
                  window.location.href = `/mentor/tracking?student=${selectedStudent._id}`;
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                View Full Progress
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StudentList; 